import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Stack, Text, TextInput, Textarea, Group } from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import { ResponsiveDrawer } from '../UI/ResponsiveDrawer';
import { DrawerHeader } from '../UI/DrawerHeader';
import { CustomMeal, MealType, PenciledMealMemory } from '../../types/mealPlan';

// One-tap starting points for the meals families repeat every week, per slot.
// They only fill the name; ingredients stay the user's own words. Leading
// emoji: these are action buttons, and buttons in this app lead with their
// glyph (leftSection); trailing is the chip-row convention (protein/cuisine
// preferences), which these are not. Uniform across the row — suggestive
// glyphs tolerated over a ragged mixed row.
const PRESETS_BY_SLOT: Record<MealType, { emoji: string; name: string }[]> = {
  breakfast: [
    { emoji: '🥣', name: 'Cereal' },
    { emoji: '🍳', name: 'Eggs & toast' },
    { emoji: '🥞', name: 'Pancakes' },
    { emoji: '🥐', name: 'Muffins & pastries' },
    { emoji: '🥤', name: 'Smoothies' },
    { emoji: '🥯', name: 'Bagels' },
    { emoji: '🍓', name: 'Yogurt & fruit' },
    { emoji: '🧊', name: 'Freezer meal' },
  ],
  lunch: [
    { emoji: '🥪', name: 'Sandwiches' },
    { emoji: '🍲', name: 'Leftovers' },
    { emoji: '🥗', name: 'Salad' },
    { emoji: '🍜', name: 'Soup' },
    { emoji: '🧊', name: 'Freezer meal' },
    { emoji: '🥡', name: 'Takeout' },
    { emoji: '🍽️', name: 'Eating out' },
  ],
  dinner: [
    { emoji: '🌮', name: 'Tacos' },
    { emoji: '🍕', name: 'Pizza night' },
    { emoji: '🥡', name: 'Takeout' },
    { emoji: '🍽️', name: 'Eating out' },
    { emoji: '🍲', name: 'Leftovers' },
    { emoji: '🧊', name: 'Freezer meal' },
    { emoji: '🥞', name: 'Breakfast for dinner' },
  ],
};

// Slot-appropriate example copy for the empty form
const FORM_EXAMPLES: Record<MealType, { name: string; ingredients: string }> = {
  breakfast: { name: 'Cereal', ingredients: 'cereal\nmilk\nbananas' },
  lunch: { name: 'Sandwiches', ingredients: 'deli turkey\nbread\napples' },
  dinner: { name: 'Tacos', ingredients: '2 lbs ground beef\ntortillas\nshredded cheese' },
};

const MAX_INGREDIENTS = 25;

interface CustomMealDrawerProps {
  opened: boolean;
  onClose: () => void;
  // Steps back to the drawer this one was opened from (the swap drawer)
  onBack?: () => void;
  dayLabel: string;
  mode: 'add' | 'edit';
  /** Name of the recipe this meal will replace; nothing changes until submit */
  replacingName?: string;
  initial?: CustomMeal;
  /** Remembered penciled-in meals; personalizes the preset chips and prefill */
  savedMeals?: PenciledMealMemory[];
  /**
   * Wizard context: pick which night this meal claims. Options are the plan's
   * unclaimed days in order; when present the chosen day rides the submit meta.
   */
  daySelect?: { options: { day: string; dateLabel: string }[]; initialDay?: string };
  /** Wizard edit: un-pencil this night entirely */
  onRemove?: () => void;
  /** Which slot the meal lands on; scopes preset chips, examples, and memories */
  mealType?: MealType;
  saving: boolean;
  onSubmit: (meal: CustomMeal, meta: { usedMemory: boolean; day?: string }) => void;
}

export const CustomMealDrawer = ({
  opened,
  onClose,
  onBack,
  dayLabel,
  mode,
  replacingName,
  initial,
  savedMeals,
  daySelect,
  onRemove,
  mealType = 'dinner',
  saving,
  onSubmit,
}: CustomMealDrawerProps) => {
  const [name, setName] = useState('');
  const [ingredientsText, setIngredientsText] = useState('');
  const [note, setNote] = useState('');
  const [usedMemory, setUsedMemory] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  // What the last chip/name-match fill set the fields to. While the fields
  // still equal it (pristine), tapping another chip swaps the whole fill —
  // including clearing for a preset with no memory; once the user hand-edits,
  // chips only change the name so they can never destroy typed work.
  const [appliedFill, setAppliedFill] = useState<{ ingredients: string; note: string } | null>(null);

  // Re-seed the form each time the drawer opens for a new target
  useEffect(() => {
    if (opened) {
      setName(initial?.name ?? '');
      setIngredientsText(initial?.ingredients?.join('\n') ?? '');
      setNote(initial?.note ?? '');
      setUsedMemory(false);
      setAppliedFill(null);
      setSelectedDay(daySelect?.initialDay ?? daySelect?.options[0]?.day ?? null);
    }
    // daySelect is derived from wizard state and changes identity per render;
    // seed only on open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, initial]);

  const fieldsPristine =
    ingredientsText === (appliedFill?.ingredients ?? '') &&
    note === (appliedFill?.note ?? '');

  // Chips: the user's own remembered meals first (they re-fill ingredients
  // and note, not just the name), then the static presets, deduped by name.
  // Both are slot-scoped: penciling a breakfast offers breakfast repeats and
  // breakfast presets, never taco night. Memories without a mealType predate
  // slots and read as dinner. Emoji marks a known meal type: stock presets
  // carry theirs, and a remembered meal matching one keeps it. Custom-named
  // meals go bare — every glyph option (pencil emoji, other emojis, a Tabler
  // icon among emojis) read as either off-vocabulary or mixed design systems.
  const chips = useMemo(() => {
    const presets = PRESETS_BY_SLOT[mealType];
    const seen = new Set<string>();
    const merged: { name: string; emoji?: string; fill?: PenciledMealMemory }[] = [];
    for (const meal of savedMeals ?? []) {
      if ((meal.mealType ?? 'dinner') !== mealType) continue;
      const key = meal.name.trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const staticPreset = presets.find(p => p.name.toLowerCase() === key);
      merged.push({ name: meal.name, emoji: staticPreset?.emoji, fill: meal });
      if (merged.length >= 8) break;
    }
    for (const preset of presets) {
      if (merged.length >= 8) break;
      if (seen.has(preset.name.toLowerCase())) continue;
      seen.add(preset.name.toLowerCase());
      merged.push({ name: preset.name, emoji: preset.emoji });
    }
    return merged;
  }, [savedMeals, mealType]);

  const applyMemory = (meal: PenciledMealMemory) => {
    const ingredients = meal.ingredients.join('\n');
    const nextNote = meal.note ?? '';
    setIngredientsText(ingredients);
    setNote(nextNote);
    setAppliedFill({ ingredients, note: nextNote });
    setUsedMemory(true);
  };

  const clearFill = () => {
    setIngredientsText('');
    setNote('');
    setAppliedFill(null);
    setUsedMemory(false);
  };

  // Typing a name that matches a remembered meal exactly re-fills it, but only
  // over pristine fields so it never clobbers what the user already wrote.
  // Slot-scoped like the chips: breakfast "leftovers" should not inherit the
  // dinner leftovers memory's ingredient list.
  const handleNameChange = (nextName: string) => {
    setName(nextName);
    if (mode !== 'add' || !fieldsPristine) return;
    const match = (savedMeals ?? []).find(
      m =>
        (m.mealType ?? 'dinner') === mealType &&
        m.name.trim().toLowerCase() === nextName.trim().toLowerCase()
    );
    if (match) {
      applyMemory(match);
    }
  };

  const parsedIngredients = ingredientsText
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .slice(0, MAX_INGREDIENTS);

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSubmit(
      {
        name: name.trim().slice(0, 80),
        ingredients: parsedIngredients.map((line) => line.slice(0, 120)),
        ...(note.trim() ? { note: note.trim().slice(0, 280) } : {}),
      },
      { usedMemory, ...(daySelect && selectedDay ? { day: selectedDay } : {}) }
    );
  };

  return (
    <ResponsiveDrawer
      opened={opened}
      onClose={onClose}
      title={
        <DrawerHeader
          onBack={onBack}
          backLabel="Back to swap options"
          color="grape"
          icon={<IconPencil size={18} />}
          title={mode === 'add' ? 'Pencil in a meal' : 'Edit penciled-in meal'}
          subtitle={replacingName ? `${dayLabel} · replaces ${replacingName}` : dayLabel}
          badge={
            <Badge size="xs" variant="light" color="violet" style={{ flexShrink: 0 }}>
              Experimental
            </Badge>
          }
        />
      }
      size="md"
    >
      <Stack gap="lg">
        {daySelect && daySelect.options.length > 1 && (
          <Stack gap={6}>
            <Text size="sm" fw={500}>
              Which night?
            </Text>
            <Group gap="xs">
              {daySelect.options.map((option) => (
                <Button
                  key={option.day}
                  size="xs"
                  variant={selectedDay === option.day ? 'filled' : 'default'}
                  color="grape"
                  radius="xl"
                  onClick={() => setSelectedDay(option.day)}
                >
                  {option.day} {option.dateLabel}
                </Button>
              ))}
            </Group>
          </Stack>
        )}
        {mode === 'add' && (
          <Group gap="xs">
            {chips.map((chip) => (
              <Button
                key={chip.name}
                size="xs"
                variant={name === chip.name ? 'light' : 'default'}
                radius="xl"
                leftSection={chip.emoji}
                onClick={() => {
                  setName(chip.name);
                  if (fieldsPristine) {
                    if (chip.fill) {
                      applyMemory(chip.fill);
                    } else {
                      clearFill();
                    }
                  }
                }}
              >
                {chip.name}
              </Button>
            ))}
          </Group>
        )}

        <TextInput
          label="What are you having?"
          placeholder={FORM_EXAMPLES[mealType].name}
          value={name}
          onChange={(e) => handleNameChange(e.currentTarget.value)}
          maxLength={80}
          required
          data-autofocus
        />

        <Textarea
          label="Ingredients for the grocery list"
          description="One per line, written however you'd write them."
          placeholder={FORM_EXAMPLES[mealType].ingredients}
          value={ingredientsText}
          onChange={(e) => setIngredientsText(e.currentTarget.value)}
          autosize
          minRows={3}
          maxRows={10}
        />
        {parsedIngredients.length >= MAX_INGREDIENTS && (
          <Text size="xs" c="dimmed">
            Keeping the first {MAX_INGREDIENTS} ingredients.
          </Text>
        )}

        <TextInput
          label="Note"
          description="Anything the household should know."
          placeholder="Grandma's recipe box, red card"
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          maxLength={280}
        />

        <Button
          fullWidth
          onClick={handleSubmit}
          disabled={!name.trim()}
          loading={saving}
        >
          {mode === 'edit' ? 'Save changes' : replacingName ? 'Replace meal' : 'Pencil it in'}
        </Button>
        {onRemove && (
          <Button variant="subtle" color="red" size="sm" onClick={onRemove}>
            Un-pencil this night
          </Button>
        )}
      </Stack>
    </ResponsiveDrawer>
  );
};
